// frontend/src/utils/menuStructure.js

/**
 * ESTRUTURA DO MENU EXPANSÍVEL (ACCORDION)
 * 
 * 🔥 NOVA ABORDAGEM: 
 * - As permissões são definidas centralmente em PERMISSOES_POR_PERFIL
 * - O menu é gerado automaticamente a partir da estrutura
 * - allowedRoles é calculado dinamicamente
 * - Permissões individuais sobrescrevem as padrão
 * 
 * 🔥 RESPONSABILIDADES:
 * 1. Definir a estrutura completa do menu (sem allowedRoles)
 * 2. Definir permissões por perfil (PERMISSOES_POR_PERFIL)
 * 3. Gerar allowedRoles dinamicamente (gerarAllowedRoles)
 * 4. Combinar permissões base com individuais (combinarPermissoes)
 * 5. Filtrar por módulos ativos (filtrarMenuPorModulos)
 * 6. Filtrar por perfil E permissões individuais (filtrarMenuPorPerfil)
 * 7. Filtrar por módulos E perfil (filtrarMenuCompleto)
 * 8. Obter permissões individuais para um perfil
 */

// ==============================================
// 🔥 CONFIGURAÇÃO CENTRAL DE PERMISSÕES POR PERFIL
// ==============================================
const PERMISSOES_POR_PERFIL = {
    // Superadmin tem acesso a tudo
    Superadmin: {
        dashboard: { overview: true },
        vendas: {
            dashboard_vendas: true,
            faturas: true,
            cotacoes: true,
            recibos: true,
            guias_entrega: true,
            recebimentos: true,
            relatorios_vendas: true,
            configuracoes_vendas: true,
           
        },
        comercial: { clientes: true, fornecedores: true },
        stock: { produtos: true, criar_produto: true, servicos: true, entrada_stock: true, categorias: true, movimentos: true, inventario: true, armazens: true },
        analise: { analitico: true, relatorios: true, financeiro: true },
        admin: { utilizadores: true, empresas: true, licencas: true, logs: true, definicoes: true }
    },
    Admin: {
        dashboard: { overview: true },
        vendas: {
            dashboard_vendas: true,
            faturas: true,
            cotacoes: true,
            recibos: true,
            guias_entrega: true,
            recebimentos: true,
            relatorios_vendas: true,
            configuracoes_vendas: false,
            
        },
        comercial: { clientes: true, fornecedores: true },
        stock: { produtos: true, criar_produto: true, servicos: true, entrada_stock: true, categorias: true, movimentos: true, inventario: true, armazens: true },
        analise: { analitico: true, relatorios: true, financeiro: true },
        admin: { utilizadores: true, empresas: false, licencas: false, logs: true, definicoes: true }
    },
    Contabilista: {
        dashboard: { overview: true },
        vendas: {
            dashboard_vendas: false,
            faturas: true,
            cotacoes: true,
            recibos: true,
            guias_entrega: false,
            recebimentos: true,
            relatorios_vendas: true,
            configuracoes_vendas: false,
           
        },
        comercial: { clientes: true, fornecedores: false },
        stock: { produtos: false, criar_produto: false, servicos: false, entrada_stock: false, categorias: false, movimentos: false, inventario: false },
        analise: { analitico: false, relatorios: true, financeiro: true },
        admin: { utilizadores: false, empresas: false, licencas: false, logs: false, definicoes: false }
    },
    Comercial: {
        dashboard: { overview: true },
        vendas: {
            dashboard_vendas: true,
            faturas: false,
            cotacoes: true,
            recibos: false,
            guias_entrega: false,
            recebimentos: false,
            relatorios_vendas: true,
            configuracoes_vendas: false,
            
        },
        comercial: { clientes: true, fornecedores: true },
        stock: { produtos: true, criar_produto: false, servicos: false, entrada_stock: false, categorias: false, movimentos: false, inventario: false },
        analise: { analitico: false, relatorios: false, financeiro: false },
        admin: { utilizadores: false, empresas: false, licencas: false, logs: false, definicoes: false }
    },
    Normal: {
        dashboard: { overview: true },
        vendas: {
            dashboard_vendas: false,
            faturas: false,
            cotacoes: false,
            recibos: false,
            guias_entrega: false,
            recebimentos: false,
            relatorios_vendas: false,
            configuracoes_vendas: false,
            
        },
    comercial: { clientes: true, fornecedores: false },
        stock: { produtos: true, criar_produto: false, servicos: false, entrada_stock: false, categorias: false, movimentos: false, inventario: false },
        analise: { analitico: false, relatorios: true, financeiro: true },
        admin: { utilizadores: false, empresas: false, licencas: false, logs: false, definicoes: false }
    }
};

// ==============================================
// 🔥 LISTA DE TODOS OS PERFIS DISPONÍVEIS
// ==============================================
const PERFIS_DISPONIVEIS = ['Superadmin', 'Admin', 'Contabilista', 'Comercial', 'Normal'];

// ==============================================
// 🔥 FUNÇÃO PARA GERAR allowedRoles DINAMICAMENTE
// ==============================================
const gerarAllowedRoles = (modulo, submodulo = null) => {
    const roles = [];

    PERFIS_DISPONIVEIS.forEach(perfil => {
        const permissoes = PERMISSOES_POR_PERFIL[perfil];
        if (!permissoes) return;

        if (submodulo) {
            // Verificar submódulo específico
            if (permissoes[modulo] && permissoes[modulo][submodulo] === true) {
                roles.push(perfil);
            }
        } else {
            // Verificar módulo inteiro (qualquer submódulo ativo)
            if (permissoes[modulo]) {
                const temAlgumAtivo = Object.values(permissoes[modulo]).some(v => v === true);
                if (temAlgumAtivo) {
                    roles.push(perfil);
                }
            }
        }
    });

    return roles;
};

// ==============================================
// 🔥 FUNÇÃO PARA VERIFICAR SE PERFIL TEM PERMISSÃO BASE
// ==============================================
const perfilTemPermissaoBase = (perfil, modulo, submodulo = null) => {
    if (perfil === 'Superadmin') return true;

    const permissoes = PERMISSOES_POR_PERFIL[perfil];
    if (!permissoes) return false;

    if (submodulo) {
        return permissoes[modulo] && permissoes[modulo][submodulo] === true;
    }

    return permissoes[modulo] && Object.values(permissoes[modulo]).some(v => v === true);
};

// ==============================================
// 🔥 FUNÇÃO PARA VERIFICAR SE PERFIL TEM PERMISSÃO (COM INDIVIDUAIS)
// ==============================================
const perfilTemPermissao = (perfil, modulo, submodulo = null, permissoesIndividuais = null) => {
    // Se tiver permissões individuais, usar elas primeiro
    if (permissoesIndividuais && permissoesIndividuais[modulo] !== undefined) {
        if (submodulo) {
            return permissoesIndividuais[modulo].submodulos?.[submodulo] === true;
        }
        return permissoesIndividuais[modulo].ativo === true;
    }

    // Fallback para permissões base
    return perfilTemPermissaoBase(perfil, modulo, submodulo);
};

// ==============================================
// 🔥 ESTRUTURA DO MENU (SEM allowedRoles - GERADO DINAMICAMENTE)
// ==============================================
const menuStructure = [
    // ==============================================
    // 1. DASHBOARD - Item único
    // ==============================================
    {
        id: 'dashboard',
        label: 'Dashboard',
        icon: 'dashboard',
        isSingle: true,
        modulo: 'dashboard',
        submodulo: 'overview',
        link: 'Dashboard'
    },

    // ==============================================
    // 2. VENDAS E FATURAÇÃO - COMPLETO
    // ==============================================
    {
        id: 'vendas',
        label: 'Vendas e Faturação',
        icon: 'receipt',
        isSingle: false,
        modulo: 'vendas',
        submodulo: null,
        children: [
            {
                label: 'Dashboard de Vendas',
                icon: 'dashboard_customize',
                link: 'DashboardVendas',
                modulo: 'vendas',
                submodulo: 'dashboard_vendas'
            },
            
            {
                label: 'Cotações',
                icon: 'request_quote',
                link: 'Cotações',
                modulo: 'vendas',
                submodulo: 'cotacoes'
            },

            {
                label: 'Faturas',
                icon: 'invoice',
                link: 'Faturas',
                modulo: 'vendas',
                submodulo: 'faturas'
            },
            {
                label: 'Recibos',
                icon: 'receipt_long',
                link: 'Recibos',
                modulo: 'vendas',
                submodulo: 'recibos'
            },
            {
                label: 'Guias de Entrega',
                icon: 'local_shipping',
                link: 'GuiasEntrega',
                modulo: 'vendas',
                submodulo: 'guias_entrega'
            },
            {
                label: 'Recebimentos',
                icon: 'payments',
                link: 'Recebimentos',
                modulo: 'vendas',
                submodulo: 'recebimentos'
            },
            
            {
                label: 'Relatório de Vendas',
                icon: 'assessment',
                link: 'RelatorioVendas',
                modulo: 'vendas',
                submodulo: 'relatorios_vendas'
            },
            {
                label: 'Configurações',
                icon: 'settings',
                link: 'ConfigVendas',
                modulo: 'vendas',
                submodulo: 'configuracoes_vendas'
            }
        ]
    },

    // ==============================================
    // 3. GESTÃO COMERCIAL
    // ==============================================
    {
        id: 'comercial',
        label: 'Gestão Comercial',
        icon: 'storefront',
        isSingle: false,
        modulo: 'comercial',
        submodulo: null,
        children: [
            { label: 'Clientes', icon: 'group', link: 'Clientes', modulo: 'comercial', submodulo: 'clientes' },
            { label: 'Fornecedores', icon: 'local_shipping', link: 'Fornecedores', modulo: 'comercial', submodulo: 'fornecedores' }
        ]
    },

    // ==============================================
    // 4. GESTÃO DE STOCK
    // ==============================================
    {
        id: 'stock',
        label: 'Gestão de Stock',
        icon: 'inventory',
        isSingle: false,
        modulo: 'stock',
        submodulo: null,
        children: [
            { label: 'Listar Produtos', icon: 'list_alt', link: 'Listar Produtos', modulo: 'stock', submodulo: 'produtos' },
            { label: 'Criar Produto', icon: 'add_box', link: 'Criar Produto', modulo: 'stock', submodulo: 'criar_produto' },
            { label: 'Serviços', icon: 'build', link: 'Serviços', modulo: 'stock', submodulo: 'servicos' },
            { label: 'Entrada de Stock', icon: 'add_shopping_cart', link: 'Entrada de Stock', modulo: 'stock', submodulo: 'entrada_stock' },
            { label: 'Categorias', icon: 'category', link: 'Categorias', modulo: 'stock', submodulo: 'categorias' },
            { label: 'Movimentos', icon: 'swap_horiz', link: 'Movimentos', modulo: 'stock', submodulo: 'movimentos' },
            { label: 'Inventário', icon: 'inventory_2', link: 'Inventário', modulo: 'stock', submodulo: 'inventario' },
            { label: 'Armazéns', icon: 'warehouse', link: 'Armazéns', modulo: 'stock', submodulo: 'armazens' }
        ]
    },

    // ==============================================
    // 5. ANÁLISE E RELATÓRIOS
    // ==============================================
    {
        id: 'analise',
        label: 'Análise e Relatórios',
        icon: 'analytics',
        isSingle: false,
        modulo: 'analise',
        submodulo: null,
        children: [
            { label: 'Analítico', icon: 'show_chart', link: 'Analitico', modulo: 'analise', submodulo: 'analitico' },
            { label: 'Relatórios', icon: 'assessment', link: 'Relatorios', modulo: 'analise', submodulo: 'relatorios' },
            { label: 'Financeiro', icon: 'account_balance', link: 'Financeiro', modulo: 'analise', submodulo: 'financeiro' }
        ]
    },

    // ==============================================
    // 6. ADMINISTRAÇÃO
    // ==============================================
    {
        id: 'admin',
        label: 'Administração',
        icon: 'admin_panel_settings',
        isSingle: false,
        modulo: 'admin',
        submodulo: null,
        children: [
            { label: 'Utilizadores', icon: 'people', link: 'Usuarios', modulo: 'admin', submodulo: 'utilizadores' },
            { label: 'Empresas', icon: 'business', link: 'Empresas', modulo: 'admin', submodulo: 'empresas' },
            { label: 'Licenças', icon: 'vpn_key', link: 'Licencas', modulo: 'admin', submodulo: 'licencas' },
            { label: 'Logs', icon: 'history', link: 'Logs', modulo: 'admin', submodulo: 'logs' },
            { label: 'Definições', icon: 'settings', link: 'Definicoes', modulo: 'admin', submodulo: 'definicoes' }
        ]
    }
];

// ==============================================
// 🔥 FUNÇÃO PARA ENRIQUECER O MENU COM allowedRoles GERADOS
// ==============================================
const enriquecerMenuComPermissoes = (menu) => {
    return menu.map(item => {
        const itemEnriquecido = { ...item };

        if (item.isSingle) {
            itemEnriquecido.allowedRoles = gerarAllowedRoles(item.modulo, item.submodulo);
        } else {
            const todosRoles = new Set();
            if (item.children) {
                item.children.forEach(child => {
                    const roles = gerarAllowedRoles(child.modulo, child.submodulo);
                    roles.forEach(r => todosRoles.add(r));
                });
            }
            itemEnriquecido.allowedRoles = Array.from(todosRoles);

            if (item.children) {
                itemEnriquecido.children = item.children.map(child => ({
                    ...child,
                    allowedRoles: gerarAllowedRoles(child.modulo, child.submodulo)
                }));
            }
        }

        return itemEnriquecido;
    });
};

// ==============================================
// 🔥 MENU ENRIQUECIDO (CACHE)
// ==============================================
let menuEnriquecidoCache = null;

const getMenuEnriquecido = () => {
    if (!menuEnriquecidoCache) {
        menuEnriquecidoCache = enriquecerMenuComPermissoes(menuStructure);
    }
    return menuEnriquecidoCache;
};

// ==============================================
// 🔥 FUNÇÃO PARA OBTER TODOS OS MÓDULOS E SUBMÓDULOS COM PERMISSÕES POR PERFIL
// ==============================================
const getModulosComPermissoes = (perfil) => {
    const resultado = {};
    const permissoes = PERMISSOES_POR_PERFIL[perfil] || {};

    menuStructure.forEach(item => {
        if (item.isSingle) {
            if (!resultado[item.modulo]) {
                resultado[item.modulo] = {
                    label: item.label,
                    icon: item.icon,
                    ativo: perfilTemPermissaoBase(perfil, item.modulo, item.submodulo),
                    submodulos: {}
                };
            }
            resultado[item.modulo].submodulos[item.submodulo] = {
                label: item.label,
                ativo: perfilTemPermissaoBase(perfil, item.modulo, item.submodulo)
            };
            return;
        }

        if (item.children) {
            if (!resultado[item.modulo]) {
                resultado[item.modulo] = {
                    label: item.label,
                    icon: item.icon,
                    ativo: perfilTemPermissaoBase(perfil, item.modulo),
                    submodulos: {}
                };
            }

            item.children.forEach(child => {
                const ativo = perfilTemPermissaoBase(perfil, child.modulo, child.submodulo);
                resultado[item.modulo].submodulos[child.submodulo] = {
                    label: child.label,
                    ativo: ativo
                };
                if (ativo) {
                    resultado[item.modulo].ativo = true;
                }
            });
        }
    });

    return resultado;
};

// ==============================================
// 🔥 FUNÇÃO PARA OBTER PERMISSÕES POR PERFIL (FORMATO PARA O MODAL)
// ==============================================
const getPermissoesPorPerfil = (perfil) => {
    const permissoes = {};
    const permissoesPerfil = PERMISSOES_POR_PERFIL[perfil] || {};

    menuStructure.forEach(item => {
        if (item.isSingle) {
            permissoes[item.modulo] = {
                ativo: perfilTemPermissaoBase(perfil, item.modulo, item.submodulo),
                submodulos: {
                    [item.submodulo]: perfilTemPermissaoBase(perfil, item.modulo, item.submodulo)
                }
            };
            return;
        }

        if (item.children) {
            const submodulos = {};
            let moduloAtivo = false;

            item.children.forEach(child => {
                const ativo = perfilTemPermissaoBase(perfil, child.modulo, child.submodulo);
                submodulos[child.submodulo] = ativo;
                if (ativo) moduloAtivo = true;
            });

            permissoes[item.modulo] = {
                ativo: moduloAtivo,
                submodulos: submodulos
            };
        }
    });

    return permissoes;
};

// ==============================================
// 🔥 FUNÇÃO PARA COMBINAR PERMISSÕES (BASE + INDIVIDUAIS)
// ==============================================
const combinarPermissoes = (permissoesBase, permissoesIndividuais) => {
    if (!permissoesIndividuais || Object.keys(permissoesIndividuais).length === 0) {
        return permissoesBase;
    }

    const resultado = JSON.parse(JSON.stringify(permissoesBase));

    Object.keys(permissoesIndividuais).forEach(moduloId => {
        if (resultado[moduloId]) {
            const ind = permissoesIndividuais[moduloId];

            if (ind.ativo !== undefined) {
                resultado[moduloId].ativo = ind.ativo;
            }

            if (ind.submodulos) {
                Object.keys(ind.submodulos).forEach(subId => {
                    if (resultado[moduloId].submodulos[subId] !== undefined) {
                        resultado[moduloId].submodulos[subId] = ind.submodulos[subId];
                    }
                });
            }
        }
    });

    return resultado;
};

// ==============================================
// 🔥 FUNÇÃO PARA FILTRAR MENU POR PERFIL + PERMISSÕES INDIVIDUAIS
// ==============================================
const filtrarMenuPorPerfil = (menu, perfil, permissoesIndividuais = null) => {
    if (!menu || menu.length === 0) {
        console.log('⚠️ filtrarMenuPorPerfil: menu vazio');
        return [];
    }

    if (perfil === 'Superadmin') {
        console.log('👑 Superadmin - menu completo (perfil)');
        return menu;
    }

    console.log(`🔍 filtrarMenuPorPerfil - Perfil: ${perfil}`);
    if (permissoesIndividuais) {
        console.log('🔍 Com permissões individuais:', Object.keys(permissoesIndividuais));
    }

    return menu
        .map(item => {
            // Verificar permissão do item principal
            let podeVerItem = false;

            if (item.isSingle) {
                // Para items únicos, verificar permissão direta
                podeVerItem = perfilTemPermissao(perfil, item.modulo, item.submodulo, permissoesIndividuais);
            } else {
                // Para items com children, verificar se tem algum child visível
                if (item.children) {
                    const childrenVisiveis = item.children.filter(child => {
                        return perfilTemPermissao(perfil, child.modulo, child.submodulo, permissoesIndividuais);
                    });
                    podeVerItem = childrenVisiveis.length > 0;
                }
            }

            if (!podeVerItem) {
                console.log(`🔒 ${item.id} - perfil ${perfil} NÃO permitido`);
                return null;
            }

            console.log(`✅ ${item.id} - perfil ${perfil} permitido`);

            if (item.isSingle) {
                return item;
            }

            const childrenFiltrados = item.children?.filter(child => {
                const podeVerChild = perfilTemPermissao(perfil, child.modulo, child.submodulo, permissoesIndividuais);
                if (!podeVerChild) {
                    console.log(`🔒 ${child.link} - perfil ${perfil} NÃO permitido`);
                }
                return podeVerChild;
            }) || [];

            if (childrenFiltrados.length === 0) {
                console.log(`🔒 ${item.id} - sem children visíveis para perfil ${perfil}`);
                return null;
            }

            console.log(`✅ ${item.id} - ${childrenFiltrados.length} children visíveis para perfil ${perfil}`);
            return {
                ...item,
                children: childrenFiltrados
            };
        })
        .filter(item => item !== null);
};

// ==============================================
// FUNÇÕES EXISTENTES (MANTIDAS)
// ==============================================

const filtrarMenuPorModulos = (empresa, menu = null) => {
    const menuBase = menu || getMenuEnriquecido();

    if (!empresa) {
        console.log('⚠️ filtrarMenuPorModulos: empresa não fornecida');
        return [];
    }

    if (empresa.perfil === 'Superadmin') {
        console.log('👑 Superadmin - menu completo');
        return menuBase;
    }

    const modulosAtivos = empresa.configuracoes?.modulosAtivos || {};

    console.log('📊 filtrarMenuPorModulos - Módulos ativos:', Object.keys(modulosAtivos));

    return menuBase
        .map(item => {
            const moduloAtivo = modulosAtivos[item.modulo]?.ativo === true;

            if (!moduloAtivo) {
                console.log(`🔒 Módulo ${item.modulo} INATIVO - removendo ${item.id}`);
                return null;
            }

            if (item.isSingle) {
                console.log(`✅ Item único ${item.id} - módulo ativo`);
                return item;
            }

            const childrenFiltrados = item.children?.filter(child => {
                const submoduloAtivo = modulosAtivos[child.modulo]?.submodulos?.[child.submodulo] === true;
                if (!submoduloAtivo) {
                    console.log(`🔒 Submódulo ${child.modulo}.${child.submodulo} INATIVO - removendo ${child.link}`);
                }
                return submoduloAtivo;
            }) || [];

            if (childrenFiltrados.length === 0) {
                console.log(`🔒 ${item.id} - sem children ativos - removendo`);
                return null;
            }

            console.log(`✅ ${item.id} - ${childrenFiltrados.length} children ativos`);
            return {
                ...item,
                children: childrenFiltrados
            };
        })
        .filter(item => item !== null);
};

// ==============================================
// 🔥 FUNÇÃO COMBINADA: FILTRAR POR MÓDULOS E PERFIL (COM PERMISSÕES INDIVIDUAIS)
// ==============================================
const filtrarMenuCompleto = (empresa, perfil, permissoesIndividuais = null, menu = null) => {
    console.log('=========================================');
    console.log('🔍 filtrarMenuCompleto - Iniciando');
    console.log(`📌 Empresa: ${empresa?.nome || 'N/A'}`);
    console.log(`📌 Perfil: ${perfil}`);
    console.log('=========================================');

    // 1. Filtrar por módulos
    const menuPorModulos = filtrarMenuPorModulos(empresa, menu);
    console.log(`📊 Após filtro de módulos: ${menuPorModulos.length} itens`);

    // 2. Filtrar por perfil + permissões individuais
    const menuPorPerfil = filtrarMenuPorPerfil(menuPorModulos, perfil, permissoesIndividuais);
    console.log(`📊 Após filtro de perfil: ${menuPorPerfil.length} itens`);

    console.log('✅ filtrarMenuCompleto - Finalizado');
    console.log('=========================================');

    return menuPorPerfil;
};

const isMenuItemAtivo = (empresa, menuId, link = null) => {
    if (!empresa) return false;

    if (empresa.perfil === 'Superadmin') {
        return true;
    }

    const modulosAtivos = empresa.configuracoes?.modulosAtivos || {};
    const menuEnriquecido = getMenuEnriquecido();
    const menuItem = menuEnriquecido.find(item => item.id === menuId);
    if (!menuItem) return false;

    if (link) {
        const child = menuItem.children?.find(c => c.link === link);
        if (!child) return false;
        return modulosAtivos[child.modulo]?.submodulos?.[child.submodulo] === true;
    }

    return modulosAtivos[menuItem.modulo]?.ativo === true;
};

const getModuloFromMenu = (menuId, link = null) => {
    const menuEnriquecido = getMenuEnriquecido();
    const menuItem = menuEnriquecido.find(item => item.id === menuId);
    if (!menuItem) return null;

    if (link) {
        const child = menuItem.children?.find(c => c.link === link);
        if (!child) return null;
        return {
            modulo: child.modulo,
            submodulo: child.submodulo
        };
    }

    return {
        modulo: menuItem.modulo,
        submodulo: menuItem.submodulo
    };
};

// ==============================================
// 🔥 FUNÇÃO PARA VERIFICAR SE PERFIL TEM ACESSO A UM SUBMÓDULO (COM INDIVIDUAIS)
// ==============================================
const perfilTemAcessoSubmodulo = (perfil, modulo, submodulo, permissoesIndividuais = null) => {
    if (perfil === 'Superadmin') return true;

    // Verificar permissões individuais primeiro
    if (permissoesIndividuais && permissoesIndividuais[modulo]) {
        return permissoesIndividuais[modulo].submodulos?.[submodulo] === true;
    }

    // Fallback para permissões base
    const permissoes = PERMISSOES_POR_PERFIL[perfil];
    if (!permissoes) return false;
    return permissoes[modulo] && permissoes[modulo][submodulo] === true;
};

// ==============================================
// 🔥 FUNÇÃO PARA OBTER PERMISSÕES SIMPLES DE UM PERFIL
// ==============================================
const getPermissoesSimples = (perfil) => {
    return PERMISSOES_POR_PERFIL[perfil] || {};
};

// ==============================================
// EXPORTAÇÃO
// ==============================================

export {
    menuStructure,
    getMenuEnriquecido,
    filtrarMenuPorModulos,
    filtrarMenuPorPerfil,
    filtrarMenuCompleto,
    isMenuItemAtivo,
    getModuloFromMenu,
    // 🔥 NOVAS EXPORTAÇÕES
    getModulosComPermissoes,
    getPermissoesPorPerfil,
    combinarPermissoes,
    perfilTemPermissao,
    perfilTemPermissaoBase,
    perfilTemAcessoSubmodulo,
    getPermissoesSimples,
    PERMISSOES_POR_PERFIL,
    PERFIS_DISPONIVEIS,
    gerarAllowedRoles
};